import {
  characterImages,
  backgroundImages,
  articleImages
} from './gameImagesRegistry'

/**
 * Preloads a set of images and reports progress
 *
 * @param {Array} imageUrls - Image URLs to preload
 * @param {Function} onProgress - Progress callback (0-100)
 * @param {Function} onComplete - Called when all images are loaded
 * @param {Function} onError - Called on error with error message
 */
export const preloadImages = (imageUrls, onProgress, onComplete, onError) => {
  const totalImages = imageUrls.length
  let loadedImages = 0
  let lastReportedProgress = -1

  // If no images to load, complete immediately
  if (totalImages === 0) {
    onProgress(100)
    onComplete()
    return
  }

  // Throttle progress updates to avoid rapid re-renders
  const updateProgress = () => {
    const progress = Math.floor((loadedImages / totalImages) * 100)

    // Only update if progress actually changed (avoid duplicate updates)
    if (progress !== lastReportedProgress) {
      lastReportedProgress = progress
      onProgress(progress)
    }

    if (loadedImages === totalImages) {
      // Small delay to ensure final progress update renders
      setTimeout(() => onComplete(), 100)
    }
  }

  imageUrls.forEach((imageUrl) => {
    const img = new Image()

    img.onload = () => {
      // After image is loaded, decode it for instant rendering
      img
        .decode()
        .then(() => {
          loadedImages++
          updateProgress()
        })
        .catch((_decodeError) => {
          // If decode fails, still count as loaded (fallback to browser decode on render)
          loadedImages++
          updateProgress()
        })
    }

    img.onerror = (_error) => {
      // Call onError but continue with other images
      if (onError) {
        onError(`Failed to load image: ${imageUrl}`)
      }

      // Still count this image as "loaded" for progress calculation
      loadedImages++
      updateProgress()
    }

    img.src = imageUrl
  })
}

/**
 * Preloads all images needed for a specific mission
 *
 * @param {Object} missionData - The mission data containing characters and backgrounds
 * @param {Function} onProgress - Progress callback (0-100)
 * @param {Function} onComplete - Called when all images are loaded
 * @param {Function} onError - Called on error with error message
 */
export const preloadMissionImages = (
  missionData,
  onProgress,
  onComplete,
  onError
) => {
  // Extract background and character images from mission data
  const imagesToLoad = []
  const uniqueImageCheck = new Set()
  const imageNames = [] // Track image names for debugging

  // CRITICAL: Add common UI images used across all missions
  // Dialogue box background (used in all dialogues)
  const dialogueBgImg = backgroundImages.dialogueBg
  if (dialogueBgImg && !uniqueImageCheck.has(dialogueBgImg)) {
    imagesToLoad.push(dialogueBgImg)
    uniqueImageCheck.add(dialogueBgImg)
    imageNames.push('DIALOGUE_BOX_BG: dialogue_bg.webp')
  }

  // Mission background (main background used throughout missions)
  const missionBgImg = backgroundImages.missionBg
  if (missionBgImg && !uniqueImageCheck.has(missionBgImg)) {
    imagesToLoad.push(missionBgImg)
    uniqueImageCheck.add(missionBgImg)
    imageNames.push('MISSION_BG: mission_one_bg.webp')
  }

  // Digipalz Headquarters (used in intro scenes and robotoro dialogues)
  const headquartersBgImg = backgroundImages.digipalzHeadquarter
  if (headquartersBgImg && !uniqueImageCheck.has(headquartersBgImg)) {
    imagesToLoad.push(headquartersBgImg)
    uniqueImageCheck.add(headquartersBgImg)
    imageNames.push('HEADQUARTERS_BG: digipalz_headquarter.webp')
  }

  // Add article profile image from mission data if exists
  if (missionData?.article?.profile_image) {
    const profileImg = articleImages[missionData.article.profile_image]
    if (profileImg && !uniqueImageCheck.has(profileImg)) {
      imagesToLoad.push(profileImg)
      uniqueImageCheck.add(profileImg)
      imageNames.push(`ARTICLE_PROFILE: ${missionData.article.profile_image}`)
    }
  }

  // Process mission scenes to extract character images and backgrounds
  if (missionData?.scenes) {
    missionData.scenes.forEach((scene) => {
      // Add scene background
      if (scene.background) {
        const bgImage = backgroundImages[scene.background]
        if (bgImage) {
          if (!uniqueImageCheck.has(bgImage)) {
            imagesToLoad.push(bgImage)
            uniqueImageCheck.add(bgImage)
            imageNames.push(`SCENE_BG: ${scene.background}`)
          }
        } else {
          console.warn(`Background not found for: ${scene.background}`)
        }
      }

      // Add character images
      if (scene.characters) {
        Object.values(scene.characters).forEach((character) => {
          if (character.image) {
            const charImg = characterImages[character.image]
            if (charImg) {
              if (!uniqueImageCheck.has(charImg)) {
                imagesToLoad.push(charImg)
                uniqueImageCheck.add(charImg)
                imageNames.push(`CHARACTER: ${character.image}`)
              }
            }
          }
        })
      }
    })
  }

  // Convert image objects to URLs where needed and filter out any nulls
  const imageUrls = imagesToLoad.filter((img) => img != null)
  // Preload all these images
  preloadImages(imageUrls, onProgress, onComplete, onError)
}
